import Button from '@mui/material/Button'

import type { GroupRecipientList, GroupRecipientListSortableField } from 'common/api/v1/types'
import { useConfirmationDialog, useGroupListsSelector, usePageParamsFilteredSelector } from '../../utils'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { Link } from '../common/Link'

import { AppDispatch, useRoutes } from '../../store'
import Toolbar from '@mui/material/Toolbar'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import {
  ListGroupListFilterKey,
  makeListGroupListsFilter,
  mapGroupListFilterToUrlParam,
  mapUrlParamToGroupListFilter,
} from './listGroupListsFilter'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsIcon from '@mui/icons-material/Settings'
import { ActionMenu } from '../common/Table/ActionMenu'
import { useDispatch } from 'react-redux'
import { removeGroupList } from '../../redux/actions/groupListsActions'

export const ListView = () => {
  const routes = useRoutes()
  const { groupLists, total, loading } = usePageParamsFilteredSelector(useGroupListsSelector)
  const showConfirmation = useConfirmationDialog()
  const dispatch = useDispatch<AppDispatch>()

  const tableConfig: TableConfig<GroupRecipientList, GroupRecipientListSortableField> = [
    {
      title: 'name',
      getValue: (groupList) => <Link to={routes.groupListsUpdate({ id: groupList.id })}>{groupList.name}</Link>,
      sorting: { byParameter: 'name' },
    },
    {
      title: 'description',
      getValue: ({ description }) => description,
      sorting: { byParameter: 'description' },
    },
    {
      getValue: (groupList) => (
        <ActionMenu
          options={[
            {
              label: 'Edit',
              icon: <SettingsIcon />,
              href: routes.groupListsUpdate({ id: groupList.id }),
            },
            {
              label: 'Remove',
              icon: <DeleteIcon />,
              isDestructive: true,
              onClick: () => {
                showConfirmation(
                  () => void dispatch(removeGroupList(groupList.id)),
                  `Are you sure you want to remove group list ${groupList.name}?`,
                )
              },
            },
          ]}
        />
      ),
      props: { align: 'right' },
    },
  ]

  const filters = makeListGroupListsFilter()
  return (
    <Wrapper
      name={['Groups', 'Lists']}
      actions={
        <Link key="groupListCreate" type="button" to={routes.groupListsNew()}>
          <Button variant="contained" color="primary">
            Add group list
          </Button>
        </Link>
      }
    >
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'groupLists'}
            mapUrlParamToFilterFn={mapUrlParamToGroupListFilter}
            mapFilterToUrlParamFn={mapGroupListFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListGroupListFilterKey.name)!}
          />
        </div>
      </Toolbar>
      <Table<GroupRecipientList>
        id="grouplists-table"
        emptyMessageComponent={
          <MissingContent message="No group lists" buttonText="Add group list" url={routes.groupListsNew()} />
        }
        config={tableConfig}
        data={groupLists}
        pending={loading}
        pagination={{ total, useUrlSearchParams: true }}
      />
    </Wrapper>
  )
}
